import React, { useState, useEffect, useContext } from "react";
import { Box } from "@mui/joy";
import { useTranslation } from 'react-i18next';
import { LegalEntityAvatar, Tag, VehicleAvatar } from "../../../../mtska-frontend-app-component/components/Snippet";
import { Icon } from "src/mtska-frontend-app-component/assets";
import { useFormatter } from "src/mtska-frontend-app-component/utils";
import { Text } from "../../../../mtska-frontend-app-component/components/Typography";
import { useDrawer } from "../../../../mtska-frontend-app-component/components/Drawer";
import { AppContext } from "src/mtska-frontend-app-scaffolding";
import { Button } from "../../../../mtska-frontend-app-component/components/Button";
import { useDataProvider } from "src/mtska-frontend-data-provider";

const dataProviderName = 'cars/car';
const drawerRouteBase = 'cars';

/** Merge list view row (_related_*) with detail payload for card display. */
const mergeCarFromView = (itemview, detail = null) => {
    if (!itemview) return null;

    const merged = detail?._id === itemview._id
        ? { ...itemview, ...detail }
        : { ...itemview };

    if (!merged.setup?.make && itemview._related_setup_make) {
        merged.setup = {
            ...(merged.setup || {}),
            make: itemview._related_setup_make,
            model: itemview._related_setup_model,
            setup: itemview._related_setup_setup,
        };
    }

    if (!merged.contract?.start_at && itemview._related_contract_start_at) {
        merged.contract = {
            ...(merged.contract || {}),
            start_at: itemview._related_contract_start_at,
            end_at: itemview._related_contract_end_at,
            contractDuration: itemview._related_contract_contractDuration ?? merged.contract?.contractDuration,
            contractMileage: itemview._related_contract_contractMileage ?? merged.contract?.contractMileage,
            renter: merged.contract?.renter ?? (itemview._related_renter_name ? {
                name: itemview._related_renter_name,
                logo: itemview._related_renter_logo ? { url: itemview._related_renter_logo } : undefined,
                telephone: itemview._related_renter_telephone,
            } : undefined),
        };
    }

    if (!merged.traceKmsLast?.value && itemview.traceKmsLast_km != null) {
        merged.traceKmsLast = {
            ...(merged.traceKmsLast || {}),
            value: itemview.traceKmsLast_km,
            traced_at: itemview.traceKmsLast_at ?? itemview.traceKmsLast_at_readable,
        };
    }

    return merged;
};

const VehicleCardFromView = ({ itemview, driver, size = "standard" }) => {
    const { t } = useTranslation();
    const { formatDate, formatDistance } = useFormatter();
    const { activate } = useDrawer();
    const { item: car, loadOne } = useDataProvider(dataProviderName, false, itemview?._id);
    const [item, setItem] = useState(() => mergeCarFromView(itemview));

    useEffect(() => {
        if (!itemview?._id) return;
        loadOne(itemview._id);
    }, [itemview?._id, loadOne]);

    useEffect(() => {
        setItem(mergeCarFromView(itemview, car?.data?._id === itemview?._id ? car.data : null));
    }, [car, itemview]);

    const handleCardClick = () => {
        if (itemview.status == 'running' && itemview.isPool != 'true') {
            activate(drawerRouteBase + '/detail', { id: itemview._id });
        } else if (itemview.status == 'running' && itemview.isPool == 'true' && item.calendar?._id && item.calendar?.status == 'active') { // NON ABBIAMO ITEMVIEW.calendar
            activate(drawerRouteBase + '/carCalendar', { car: item, driver: driver });
        }
    }

    const handleAddTrack = () => {
        //console.info('handleAddTrack button, car cliccata', item.plate, itemview.plate)
        activate('tracekms/create', { car: item, legalEntity: item.legalEntity, numberOfTrackFields: 1 });
    }

    const handleFileAdd = () => {
        activate('cars/genericFileAssociation', { car: item, multipart: true, filters: { data: { modelName: 'cars', _id: item?.data?._id, keyName: item?.data?.plate } } });
    }

    const tagcolors = {
        installed: "success",
        available: "warning",
        running: "success",
        in_order: "secondary",
        in_offer: "secondary",
        in_configuration: "secondary",
        closed: "danger",
        preleasing: "warning",
        substitute: "warning",
        archived: "danger"
    }

    const getStatus = (v) => {
        return v?.status ? v?.status.toLowerCase() : 'running';
    }

    const { appConfiguration } = useContext(AppContext);

    const addDays = (date, days) => {
        let result = new Date(date);
        result.setDate(result.getDate() + days);
        return result;
    }

    const getTraceStatus = () => {
        if (itemview.status != 'running') return 'neutral';

        const traced = new Date(item?.traceKmsLast?.traced_at ?? item?.traceKmsLast_km);
        const td = new Date();
        const nexttraced = addDays(traced, parseInt(appConfiguration.custom.traceKmMaxMissingDays));

        const awaitenlapsed = Math.floor((td - traced) / (1000 * 60 * 60 * 24));
        const awaitnext = Math.floor((nexttraced - traced) / (1000 * 60 * 60 * 24));

        const awaitenlapsedpercentage = awaitenlapsed / awaitnext;

        let color = "neutral";
        if (awaitenlapsedpercentage >= 1) {
            color = "danger"
        } else if (awaitenlapsedpercentage >= 0.7) {
            color = "warning"
        } else if (awaitenlapsedpercentage < 0.7) {
            color = "success"
        }
        //console.info('qui', traced, td, nexttraced, awaitenlapsed, awaitnext, awaitenlapsedpercentage, color);

        return color;
    }

    const getDeliveryDate = () => {
        let deliveryDate = null;
        if (item?.contract?.deliveryDelayed3_at) {
            deliveryDate = item?.contract?.deliveryDelayed3_at;
        } else if (item?.contract?.deliveryDelayed2_at) {
            deliveryDate = item?.contract?.deliveryDelayed2_at;
        } else if (item?.contract?.deliveryDelayed1_at) {
            deliveryDate = item?.contract?.deliveryDelayed1_at;
        } else if (item?.contract?.estimatedDelivery_at) {
            deliveryDate = item?.contract?.estimatedDelivery_at;
        }

        return formatDate(deliveryDate);
    }

    return (
        item && <Box className={"vehicle-card-wrapper"}>
            <Box className={"vehicle-card vehicle-avatar-" + size}>
                <Box className={itemview.status == 'running' && "vehicle-card-clickable"} >

                    <VehicleAvatar url={item.picture?.url} fullname={item?.setup?.make + " " + item?.setup?.model} detail={item?.setup?.setup} item={item} withname={true} />
                    {itemview.status == 'running' && (
                        <>
                            <Box className="clickableIconWrapper" onClick={handleCardClick}>
                                {itemview.isPool == 'true' && item?.calendar?.status == 'paused' ? (
                                    <Box className="ribbon warning">{t('Booking paused')}</Box>
                                ) : (
                                    <Icon icon='faArrowUpRightFromSquare' className="clickableIcon" />
                                )}
                            </Box>
                            <Box className="info">
                                <LegalEntityAvatar name={item?.contract?.renter?.name} url={item?.contract?.renter?.logo?.url} size="small" withname={true} detail={item?.contract?.renter?.telephone ?? t('No telephone registered')} />
                            </Box>
                        </>
                    )}
                    {itemview.status == 'in_order' && item?.contract?.renter && (
                        <>
                            <Box className="info">
                                <LegalEntityAvatar name={item?.contract?.renter?.name} url={item?.contract?.renter?.logo?.url} size="small" withname={true} />
                            </Box>

                            <Box className="info">
                                <p className="label">{t('estimatedDelivery_at')}</p>
                                <p className="value">{getDeliveryDate()}</p>
                            </Box>
                        </>
                    )}

                    <Box className={"file-add"}>
                        <Button className="addFile" onClick={handleFileAdd} variant={"solid"} icon={'faFilePlus'} title={t("Add a file to the vehicle")}></Button>
                    </Box>

                    <Box className="info">
                        <p className="label">{t('plate')}</p>
                        <p className="value">{item?.plate || (<Tag color={"warning"} title={t("No plate yet assigned")}><Icon icon="faWarning" />{t('No plate yet assigned')}</Tag>)} {item.isPool == 'true' && <span style={{ fontSize: '12px', color: '#DEDEDE' }}>({t('PoolVehicle')})</span>}</p>
                    </Box>

                    <Box className="info">
                        <p className="label">{t('status')}</p>
                        <Tag color={(tagcolors[getStatus(item)])} size="small" label={t(getStatus(item))} />
                    </Box>
                    {itemview.status != 'in_order' && (
                        <Box className="info highlight">
                            {
                                item?.contract?.start_at && (
                                    <Text icon="faArrowRightFromLine" className="info-contract">{formatDate(item?.contract?.start_at)}</Text>
                                )
                            }

                            {
                                item?.contract?.end_at && (
                                    <Text icon="faArrowRightToLine" className="info-contract">{formatDate(item?.contract?.end_at)}</Text>
                                )
                            }

                            {
                                item?.contract?.contractDuration && (
                                    <Text icon="faHourglass" className="info-contract">{item?.contract?.contractDuration} {t('months')}</Text>
                                )
                            }

                            {
                                item?.contract?.contractMileage && (
                                    <Text icon="faRoad" className="info-contract">{formatDistance(item?.contract?.contractMileage)}</Text>
                                )
                            }
                        </Box>
                    )}
                </Box>
                {itemview.status == 'running' && (
                    //SE oggi minore di 6 mesi da scadenza revisione strip rossa con countdown
                    <></>
                )}
                <Box className={"info info-tracekm info-tracekm-" + getTraceStatus()}>
                    {itemview.status == 'running' && (
                        <Button className="addTrack" onClick={handleAddTrack} variant={"solid"} icon={getTraceStatus() != 'success' ? "faWarning" : 'faPlus'}></Button>
                    )}
                    <p className="label">{t('Last recorded mileage')}</p>
                    <p className="value primary">{formatDistance(item?.traceKmsLast?.value ?? item?.traceKmsLast_km)}</p>
                    <p className="label">{t('Last recorded date')}</p>
                    <p className="value secondary" title={item?.setup?.setup}>{formatDate(item?.traceKmsLast?.traced_at ?? item?.traceKmsLast_at)}</p>
                </Box>
            </Box>
        </Box>
    )
}

export default VehicleCardFromView;